import {
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  RESULT_CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { MatrixView } from "@/components/tools/linear-algebra/matrix-view";
import type { MATRIX_CALCULATOR_STRINGS } from "@/components/tools/matrix-calculator";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type Cofactor,
  cofactorExpansion,
  colCount,
  det2,
  eliminateForDeterminant,
  formatMatrixText,
  identity,
  invertMatrix,
  type Matrix,
  matricesEqual,
  multiplyMatrix,
  nullSpace,
  R_ONE,
  type ReductionStep,
  type RowOp,
  reduceToRref,
  trace,
} from "@/lib/tool-kit/linear-algebra/matrix";
import {
  equalsR,
  formatStyled,
  type NumberStyle,
  negR,
  type Rational,
  signR,
} from "@/lib/tool-kit/math/rational";

type Strings = typeof MATRIX_CALCULATOR_STRINGS;

export interface MatrixCtx {
  strings: Strings;
  style: NumberStyle;
  mark: string;
  locale: string;
  fmt: (value: number) => string;
}

const NEEDS_BRACKETS = /^−|\//;

function value(ctx: MatrixCtx, x: Rational): string {
  return formatStyled(x, ctx.style, ctx.mark);
}

/** "(−2)" and "(1/2)" where a number sits next to an operator. */
function bracketed(ctx: MatrixCtx, x: Rational): string {
  const text = value(ctx, x);
  return NEEDS_BRACKETS.test(text) ? `(${text})` : text;
}

export function opText(op: RowOp, ctx: MatrixCtx): string {
  const { strings } = ctx;
  if (op.kind === "swap") {
    return t(strings.opSwap, { a: op.a + 1, b: op.b + 1 });
  }
  if (op.kind === "scale") {
    return t(strings.opScale, {
      row: op.row + 1,
      factor: bracketed(ctx, op.factor),
    });
  }
  const negative = signR(op.factor) < 0;
  const magnitude = negative ? negR(op.factor) : op.factor;
  const unit = equalsR(magnitude, R_ONE);
  let template: string = negative ? strings.opSubtract : strings.opAdd;
  if (unit) {
    template = negative ? strings.opSubtractOne : strings.opAddOne;
  }
  return t(template, {
    row: op.row + 1,
    source: op.source + 1,
    factor: bracketed(ctx, magnitude),
  });
}

/* ---------------------------------------------------------------- pieces */

function View({
  matrix,
  ctx,
  label,
  pivot,
  split,
}: {
  matrix: Matrix;
  ctx: MatrixCtx;
  label: string;
  pivot?: { row: number; col: number };
  split?: number;
}) {
  return (
    <MatrixView
      label={label}
      mark={ctx.mark}
      matrix={matrix}
      pivot={pivot}
      split={split}
      style={ctx.style}
    />
  );
}

/** The answer matrix in a result card, with a copy button. */
export function AnswerMatrix({
  matrix,
  title,
  ctx,
}: {
  matrix: Matrix;
  title: string;
  ctx: MatrixCtx;
}) {
  const { strings } = ctx;
  return (
    <div className={`${RESULT_CARD} space-y-3`}>
      <p className="font-medium text-foreground text-sm">{title}</p>
      <View ctx={ctx} label={title} matrix={matrix} />
      <p className="text-muted-foreground text-xs">
        {t(strings.dimensions, {
          rows: ctx.fmt(matrix.length),
          cols: ctx.fmt(colCount(matrix)),
        })}
      </p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyMatrix}
        small
        text={formatMatrixText(matrix, ctx.style, ctx.mark)}
      />
    </div>
  );
}

/** Every row operation with the matrix it produces. */
export function StepList({
  start,
  steps,
  split,
  ctx,
}: {
  start: Matrix;
  steps: readonly ReductionStep[];
  split?: number;
  ctx: MatrixCtx;
}) {
  const { strings } = ctx;
  if (steps.length === 0) {
    return <Notice>{strings.noSteps}</Notice>;
  }
  return (
    <details
      className="rounded-lg border border-border bg-card p-4"
      open={steps.length <= 12}
    >
      <summary className="cursor-pointer font-medium text-foreground text-sm">
        {tPlural(strings.stepsSummary, ctx.locale, steps.length, {
          count: ctx.fmt(steps.length),
        })}
      </summary>
      <ol className="mt-4 space-y-4">
        <li className="space-y-1">
          <p className="text-muted-foreground text-sm">{strings.startMatrix}</p>
          <View
            ctx={ctx}
            label={strings.startMatrix}
            matrix={start}
            split={split}
          />
        </li>
        {steps.map((step, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: steps are an ordered log
          <li className="space-y-1" key={index}>
            <p className="font-mono text-foreground text-sm">
              <span className="text-muted-foreground">
                {t(strings.stepNumber, { n: ctx.fmt(index + 1) })}{" "}
              </span>
              {opText(step.op, ctx)}
            </p>
            <View
              ctx={ctx}
              label={t(strings.afterStep, { n: ctx.fmt(index + 1) })}
              matrix={step.matrix}
              pivot={step.pivot}
              split={split}
            />
          </li>
        ))}
      </ol>
    </details>
  );
}

function Check({ children }: { children: string }) {
  return <p className="text-muted-foreground text-sm">{children}</p>;
}

/* ----------------------------------------------------- element-wise working */

function sumExpression(
  ctx: MatrixCtx,
  a: Matrix,
  b: Matrix,
  i: number,
  j: number,
  sign: "+" | "−"
): string {
  return `${bracketed(ctx, a[i][j])} ${sign} ${bracketed(ctx, b[i][j])}`;
}

function productExpression(
  ctx: MatrixCtx,
  a: Matrix,
  b: Matrix,
  i: number,
  j: number
) {
  return a[i]
    .map((x, k) => `${bracketed(ctx, x)} × ${bracketed(ctx, b[k][j])}`)
    .join(" + ");
}

export function EntryWorking({
  lines,
  ctx,
}: {
  lines: readonly {
    i: number;
    j: number;
    expression: string;
    result: Rational;
  }[];
  ctx: MatrixCtx;
}) {
  const { strings } = ctx;
  return (
    <details
      className="rounded-lg border border-border bg-card p-4"
      open={lines.length <= 9}
    >
      <summary className="cursor-pointer font-medium text-foreground text-sm">
        {strings.entryWorking}
      </summary>
      <ul className="mt-3 space-y-1 font-mono text-muted-foreground text-sm">
        {lines.map((line) => (
          <li key={`${line.i}-${line.j}`}>
            {t(strings.entryLine, {
              i: ctx.fmt(line.i + 1),
              j: ctx.fmt(line.j + 1),
              expression: line.expression,
              result: value(ctx, line.result),
            })}
          </li>
        ))}
      </ul>
    </details>
  );
}

export function entryLines(
  kind: "add" | "subtract" | "multiply" | "scalar",
  a: Matrix,
  b: Matrix,
  result: Matrix,
  scalar: string,
  ctx: MatrixCtx
) {
  return result.flatMap((row, i) =>
    row.map((cell, j) => {
      let expression = "";
      if (kind === "add") {
        expression = sumExpression(ctx, a, b, i, j, "+");
      } else if (kind === "subtract") {
        expression = sumExpression(ctx, a, b, i, j, "−");
      } else if (kind === "multiply") {
        expression = productExpression(ctx, a, b, i, j);
      } else {
        expression = `${scalar} × ${bracketed(ctx, a[i][j])}`;
      }
      return { i, j, expression, result: cell };
    })
  );
}

/* ------------------------------------------------------------ determinant */

function CofactorTable({
  cofactor,
  ctx,
}: {
  cofactor: Cofactor;
  ctx: MatrixCtx;
}) {
  const { strings } = ctx;
  const sum = cofactor.terms
    .map((term, index) => {
      const entry = bracketed(ctx, term.entry);
      const minor = value(ctx, term.minorDet);
      let sign = term.sign > 0 ? "+ " : "− ";
      if (index === 0) {
        sign = term.sign > 0 ? "" : "−";
      }
      return `${index === 0 ? "" : " "}${sign}${entry} × (${minor})`;
    })
    .join("");
  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead>
            <tr>
              {[
                strings.colEntry,
                strings.colSign,
                strings.colMinor,
                strings.colMinorDet,
                strings.colTerm,
              ].map((h) => (
                <th className={TH} key={h} scope="col">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cofactor.terms.map((term) => (
              <tr key={term.col}>
                <td className={`${TD} font-mono`}>{value(ctx, term.entry)}</td>
                <td className={`${TD} font-mono`}>
                  {term.sign > 0 ? "+" : "−"}
                </td>
                <td className={TD}>
                  <View
                    ctx={ctx}
                    label={t(strings.minorLabel, {
                      col: ctx.fmt(term.col + 1),
                    })}
                    matrix={term.minor}
                  />
                </td>
                <td className={`${TD} font-mono`}>
                  {value(ctx, term.minorDet)}
                </td>
                <td className={`${TD} font-mono`}>
                  {value(ctx, term.contribution)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="font-mono text-foreground text-sm">
        {t(strings.cofactorSum, { sum, result: value(ctx, cofactor.value) })}
      </p>
    </div>
  );
}

function DeterminantWorking({ a, ctx }: { a: Matrix; ctx: MatrixCtx }) {
  const { strings } = ctx;
  const n = a.length;
  if (n === 1) {
    return <Check>{t(strings.det1, { a: value(ctx, a[0][0]) })}</Check>;
  }
  if (n === 2) {
    return (
      <p className="font-mono text-foreground text-sm">
        {t(strings.det2, {
          a: bracketed(ctx, a[0][0]),
          b: bracketed(ctx, a[0][1]),
          c: bracketed(ctx, a[1][0]),
          d: bracketed(ctx, a[1][1]),
          result: value(ctx, det2(a)),
        })}
      </p>
    );
  }
  if (n === 3) {
    return (
      <>
        <p className="text-muted-foreground text-sm">{strings.cofactorIntro}</p>
        <CofactorTable cofactor={cofactorExpansion(a)} ctx={ctx} />
      </>
    );
  }
  const elimination = eliminateForDeterminant(a);
  const diagonal = elimination.triangular
    .map((row, i) => bracketed(ctx, row[i]))
    .join(" × ");
  return (
    <>
      <p className="text-muted-foreground text-sm">
        {strings.eliminationIntro}
      </p>
      <StepList ctx={ctx} start={a} steps={elimination.steps} />
      <p className="font-mono text-foreground text-sm">
        {t(strings.eliminationProduct, {
          swaps: ctx.fmt(elimination.swaps),
          sign: elimination.swaps % 2 === 0 ? "+1" : "−1",
          diagonal,
          result: value(ctx, elimination.determinant),
        })}
      </p>
    </>
  );
}

export function DeterminantResult({ a, ctx }: { a: Matrix; ctx: MatrixCtx }) {
  const { strings } = ctx;
  const det = a.length === 1 ? a[0][0] : eliminateForDeterminant(a).determinant;
  const zero = det.num === BigInt(0);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.determinantLabel}
          value={value(ctx, det)}
        />
        <StatCard
          label={strings.invertibleLabel}
          value={zero ? strings.no : strings.yes}
        />
      </StatGrid>
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <DeterminantWorking a={a} ctx={ctx} />
      <Check>{zero ? strings.detZeroNote : strings.detNonzeroNote}</Check>
    </>
  );
}

/* ------------------------------------------------------------------ inverse */

export function InverseResult({ a, ctx }: { a: Matrix; ctx: MatrixCtx }) {
  const { strings } = ctx;
  const n = a.length;
  const result = invertMatrix(a);
  const { reduction } = result;
  const missing = Array.from({ length: n }, (_, c) => c).filter(
    (c) => !reduction.pivotCols.includes(c)
  );
  return (
    <>
      {result.inverse ? (
        <AnswerMatrix
          ctx={ctx}
          matrix={result.inverse}
          title={strings.inverseTitle}
        />
      ) : (
        <Notice>
          {t(strings.singularNote, {
            cols: missing.map((c) => ctx.fmt(c + 1)).join(", "),
          })}
        </Notice>
      )}
      {result.inverse &&
      matricesEqual(multiplyMatrix(a, result.inverse), identity(n)) ? (
        <Check>{strings.inverseCheck}</Check>
      ) : null}
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <p className="text-muted-foreground text-sm">{strings.inverseIntro}</p>
      <StepList
        ctx={ctx}
        split={n}
        start={result.augmented}
        steps={reduction.steps}
      />
    </>
  );
}

/* --------------------------------------------------------------------- RREF */

export function RrefResult({
  a,
  ctx,
  showRank,
}: {
  a: Matrix;
  ctx: MatrixCtx;
  showRank: boolean;
}) {
  const { strings } = ctx;
  const reduction = reduceToRref(a);
  const rankValue = reduction.pivotCols.length;
  const cols = colCount(a);
  const basis = nullSpace(a);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis={showRank}
          label={strings.rankLabel}
          value={ctx.fmt(rankValue)}
        />
        <StatCard
          label={strings.nullityLabel}
          value={ctx.fmt(cols - rankValue)}
        />
        <StatCard
          label={strings.pivotColumnsLabel}
          value={
            reduction.pivotCols.length === 0
              ? strings.none
              : reduction.pivotCols.map((c) => ctx.fmt(c + 1)).join(", ")
          }
        />
      </StatGrid>
      <AnswerMatrix
        ctx={ctx}
        matrix={reduction.result}
        title={strings.rrefTitle}
      />
      <Check>{strings.rankNote}</Check>
      {basis.length > 0 ? (
        <div className="space-y-2">
          <h3 className={WIDGET_H3}>{strings.nullSpaceHeading}</h3>
          <div className="flex flex-wrap gap-4">
            {basis.map((vector) => (
              <View
                ctx={ctx}
                key={vector.map((x) => `${x.num}/${x.den}`).join(",")}
                label={strings.nullVectorLabel}
                matrix={vector.map((x) => [x])}
              />
            ))}
          </div>
        </div>
      ) : null}
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <StepList ctx={ctx} start={a} steps={reduction.steps} />
    </>
  );
}

/* ------------------------------------------------------------------- trace */

export function TraceResult({ a, ctx }: { a: Matrix; ctx: MatrixCtx }) {
  const { strings } = ctx;
  const parts = a.map((row, i) => bracketed(ctx, row[i])).join(" + ");
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.traceLabel}
          value={value(ctx, trace(a))}
        />
      </StatGrid>
      <p className="font-mono text-foreground text-sm">
        {t(strings.traceWorking, { parts, result: value(ctx, trace(a)) })}
      </p>
    </>
  );
}
